import { useState, useEffect, useCallback } from 'react';
import { BulkDraftState } from '@/types/bulk';

interface UseBulkDraftOptions {
  key: string; // 'bulk_equipes' ou 'bulk_membres'
  entrepriseId: string;
  debounceMs?: number; // Délai avant sauvegarde auto (défaut: 1000ms)
}

interface UseBulkDraftReturn<T> {
  draft: T[];
  setDraft: (data: T[]) => void;
  clearDraft: () => void;
  hasPendingDraft: boolean;
  lastSaved: Date | null;
  restoreDraft: () => T[] | null;
}

export function useBulkDraft<T extends { id: string }>({
  key,
  entrepriseId,
  debounceMs = 1000,
}: UseBulkDraftOptions): UseBulkDraftReturn<T> {
  const storageKey = `${key}_draft_${entrepriseId}`;

  const [draft, setDraftInternal] = useState<T[]>([]);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasPendingDraft, setHasPendingDraft] = useState(false);

  // Vérifier s'il existe un brouillon au montage
  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed: BulkDraftState<T> = JSON.parse(stored);
        // Vérifier que le brouillon est récent (moins de 24h)
        const savedDate = new Date(parsed.lastSaved);
        const now = new Date();
        const hoursDiff = (now.getTime() - savedDate.getTime()) / (1000 * 60 * 60);

        if (hoursDiff < 24 && parsed.data.length > 0) {
          setHasPendingDraft(true);
        }
      } catch (e) {
        localStorage.removeItem(storageKey);
      }
    }
  }, [storageKey]);

  // Sauvegarde automatique avec debounce
  useEffect(() => {
    if (draft.length === 0) return;

    const timer = setTimeout(() => {
      const state: BulkDraftState<T> = {
        data: draft,
        lastSaved: new Date().toISOString(),
        entrepriseId,
      };
      localStorage.setItem(storageKey, JSON.stringify(state));
      setLastSaved(new Date());
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [draft, storageKey, entrepriseId, debounceMs]);

  const setDraft = useCallback((data: T[]) => {
    setDraftInternal(data);
    setHasPendingDraft(false);
  }, []);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(storageKey);
    setDraftInternal([]);
    setLastSaved(null);
    setHasPendingDraft(false);
  }, [storageKey]);

  const restoreDraft = useCallback((): T[] | null => {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;

    try {
      const parsed: BulkDraftState<T> = JSON.parse(stored);
      setDraftInternal(parsed.data);
      setHasPendingDraft(false);
      return parsed.data;
    } catch (e) {
      return null;
    }
  }, [storageKey]);

  return {
    draft,
    setDraft,
    clearDraft,
    hasPendingDraft,
    lastSaved,
    restoreDraft,
  };
}
